import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { messageDeliveryStatus } from '../../Helpers/Chat/RecentChat';
import { isGroupChat } from '../../Helpers/Chat/ChatHelper';
import { IconPreviewPending } from '../../Components/Common/Icons';
import {
    MSG_PROCESSING_STATUS_ID,
    GROUP_CHAT_PROFILE_UPDATED_NOTIFY,
    MSG_SEEN_STATUS_ID,
} from '../../Helpers/Chat/Constant';
import { isLocalUser, formatGroupIdToJid } from '../../Helpers/Chat/User';
import { useGetTypingStatus } from '../../hooks/useTypingStatus';
import { useLanguage } from '../../localization/useLanguage';
import SDK from '../../Components/SDK';

const Message = props => {
    const { typingData: { id: typingId, data = [] } = {} } = useSelector(store => store);
    const { t } = useLanguage();

    const { lastMessage, publisherId, deleteStatus, onClickHandler = null } = props;
    const { messageId, chatUserType, fromUserId, msgStatus, msgType, seenReceipt } = props;

    // The composer stores a draft under the bare user or group id it is opened with, so
    // the row reads it back under the same key.
    const draft = useSelector(store => store.chatConversationHistory?.data?.[fromUserId]?.typedMessage);
    // The composer writes "" on send, so an empty draft must not win over the last message.
    const hasDraft = typeof draft === 'string' && draft.trim().length > 0;

    const { isTyping, displayName } = useGetTypingStatus({
        typingId,
        typingData: data,
        fromUserId,
        chatType: chatUserType,
    });

    useEffect(() => {
        // Get group chat status for recent chat
        if (isGroupChat(chatUserType) && isLocalUser(publisherId) && msgType !== GROUP_CHAT_PROFILE_UPDATED_NOTIFY) {
            const groupJid = formatGroupIdToJid(fromUserId);
            SDK.getGroupMsgInfo(groupJid, messageId, true);
        }
    }, []);

    /**
     * handleMsgStatusIcon() method to handle the message status icon.
     * @param {string} status
     */
    const messageStatus = (lastmessageStatus, seenReceipt, publisherid) => {
        if (!isLocalUser(publisherid)) return null;

        let statusClass = messageDeliveryStatus[lastmessageStatus];

        if (chatUserType === 'chat') {
            // If seenReceipt is null / undefined / empty → keep default statusClass
            if (seenReceipt === null || seenReceipt === undefined || seenReceipt === '') {
                return (
                    <i className={statusClass}>
                        {lastmessageStatus === MSG_PROCESSING_STATUS_ID ? <IconPreviewPending /> : ''}
                    </i>
                );
            }

            // Explicit check for seenReceipt === 0
            if (seenReceipt === 0) {
                if (lastmessageStatus === MSG_SEEN_STATUS_ID) {
                    statusClass = 'user-status-receive';
                } else {
                    statusClass = 'user-status-sent';
                }
            }
        } else {
            statusClass = messageDeliveryStatus[lastmessageStatus];
        }

        return (
            <i className={statusClass}>
                {lastmessageStatus === MSG_PROCESSING_STATUS_ID ? <IconPreviewPending /> : ''}
            </i>
        );
    };

    const status =
        msgType === GROUP_CHAT_PROFILE_UPDATED_NOTIFY || deleteStatus === 1
            ? null
            : messageStatus(msgStatus, seenReceipt, publisherId);

    const getLastMessageClass = (chatUserType, lastMessage) => {
        const isReactNode = React.isValidElement(lastMessage);
        switch (chatUserType) {
            case 'groupchat':
                return isReactNode ? 'last_msgs only_msg' : 'last_msgs';
            case 'chat':
                return isReactNode ? 'last_msgs only_msg' : 'last_msgs';
            default:
                return 'last_msgs';
        }
    };

    return (
        <span
            className="recentText"
            role="button"
            tabIndex="0"
            onClick={onClickHandler}
            onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                    onClickHandler();
                }
            }}
        >
            {isTyping ? (
                <span className="txtTyping">{displayName}</span>
            ) : hasDraft ? (
                <>
                    {/* JSX trims trailing whitespace before a closing tag, so the gap after
                        the label is set in CSS rather than written here. */}
                    <span className="draftLabel">{t('chat.preview.DRAFT')}</span>
                    <span className={getLastMessageClass(chatUserType, draft)}>{draft}</span>
                </>
            ) : (
                <>
                    {lastMessage && (
                        <>
                            {status}{' '}
                            <span className={getLastMessageClass(chatUserType, lastMessage)}>{lastMessage}</span>
                        </>
                    )}
                </>
            )}
        </span>
    );
};

export default Message;

Message.propTypes = {
    lastMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    publisherId: PropTypes.string,
    deleteStatus: PropTypes.number,
    onClickHandler: PropTypes.func,
    msgStatus: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    msgType: PropTypes.string,
    chatUserType: PropTypes.string,
    messageId: PropTypes.string,
    fromUserId: PropTypes.string,
};
